import axios from "axios";
import { getLocationSalesApi } from "../constants/apis";
import { getUserToken } from "../helpers/local_storage_helpers";

// IMPORTANT: keep the key outside of source where possible.
// Configure this in CRA as REACT_APP_OPENWEATHER_API_KEY in your .env file.
// For now, using hardcoded key as fallback if env var is not set
const OPENWEATHER_API_KEY = "a41ab53f2bd172f10bb75f5bafe845ae";

/**
 * Reverse geocode coordinates to get location details
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object|null>} Location data from Nominatim
 */
export const reverseGeocode = async (lat, lon) => {
    try {
        const config = {
            headers: {
                "Accept": "application/json",
            },
            params: {
                format: "json",
                lat: lat,
                lon: lon,
                zoom: 12,
                addressdetails: 1,
            },
        };
        const response = await axios.get(
            "https://nominatim.openstreetmap.org/reverse",
            config
        );
        return response.data;
    } catch (error) {
        console.error("Reverse geocoding error:", error);
        return null;
    }
};

/**
 * Fetch current weather and forecast data (Open-Meteo, used for the detail panels)
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} Current and forecast weather data
 */
export const fetchCurrentAndForecast = async (lat, lon) => {
    try {
        const config = {
            params: {
                latitude: String(lat),
                longitude: String(lon),
                current:
                    "temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_gusts_10m,wind_direction_10m",
                hourly:
                    "temperature_2m,relative_humidity_2m,precipitation,cloud_cover,wind_speed_10m,wind_gusts_10m,wind_direction_10m",
                daily:
                    "weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max",
                timezone: "auto",
            },
        };
        const response = await axios.get(
            "https://api.open-meteo.com/v1/forecast",
            config
        );
        return response.data;
    } catch (error) {
        console.error("Current and forecast fetch error:", error);
        throw new Error("Failed to fetch forecast");
    }
};

/**
 * Fetch past weather data for the last N days
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Historical weather data
 */
export const fetchPast = async (lat, lon, startDate, endDate) => {
    try {
        const config = {
            params: {
                latitude: String(lat),
                longitude: String(lon),
                start_date: startDate,
                end_date: endDate,
                hourly: "temperature_2m,relative_humidity_2m,precipitation",
                timezone: "auto",
            },
        };
        const response = await axios.get(
            "https://archive-api.open-meteo.com/v1/era5",
            config
        );
        return response.data;
    } catch (error) {
        console.error("Past weather fetch error:", error);
        throw new Error("Failed to fetch past weather");
    }
};

/**
 * Fetch cities/towns visible within a bounding box using Overpass API.
 * This is kept as a fallback when OpenWeather bulk API is not configured/available.
 * @param {number} minLon - Western longitude
 * @param {number} minLat - Southern latitude
 * @param {number} maxLon - Eastern longitude
 * @param {number} maxLat - Northern latitude
 * @param {number} limit - Max number of places to return
 * @returns {Promise<Array<{ id: number|string, name: string, lat: number, lon: number, place: string }>>}
 */
export const searchCitiesInBBox = async (minLon, minLat, maxLon, maxLat, limit = 15) => {
    // Helper: fallback via Nominatim viewbox
    const nominatimFallback = async () => {
        try {
            const viewbox = `${minLon},${maxLat},${maxLon},${minLat}`; // left,top,right,bottom
            const baseParams = {
                format: "jsonv2",
                bounded: 1,
                viewbox,
                addressdetails: 0,
            };
            const [cityRes, townRes] = await Promise.all([
                axios.get("https://nominatim.openstreetmap.org/search", {
                    params: { ...baseParams, q: "city", limit: Math.max(1, Math.ceil(limit / 2)) },
                    headers: { "Accept": "application/json" },
                }),
                axios.get("https://nominatim.openstreetmap.org/search", {
                    params: { ...baseParams, q: "town", limit: Math.max(1, Math.ceil(limit / 2)) },
                    headers: { "Accept": "application/json" },
                }),
            ]);
            const items = [
                ...(Array.isArray(cityRes?.data) ? cityRes.data : []),
                ...(Array.isArray(townRes?.data) ? townRes.data : []),
            ];
            return items.map((it) => ({
                id: it.osm_id || `${it.place_id}`,
                name: it.display_name?.split(",")?.[0] || it.name || "Unknown",
                lat: parseFloat(it.lat),
                lon: parseFloat(it.lon),
                place: it.type || "place",
            }));
        } catch {
            return [];
        }
    };

    try {
        // Overpass expects bbox in order: south,west,north,east
        const bbox = `${minLat},${minLon},${maxLat},${maxLon}`;
        // Keep query simple; limit results client-side for robustness
        const query = `[out:json][timeout:10];
node["place"~"^(city|town)$"]["name"](${bbox});
out body;`;
        const response = await axios.get("https://overpass-api.de/api/interpreter", {
            params: { data: query },
            headers: {
                "Accept": "application/json",
            },
        });
        const elements = (response?.data?.elements || [])
            .filter((el) => el && el.type === "node" && el.tags && el.tags.name)
            // Stable sort by population tag if present, else leave as-is
            .sort((a, b) => (parseInt(b.tags.population || "0", 10) || 0) - (parseInt(a.tags.population || "0", 10) || 0))
            .slice(0, Math.max(1, limit));
        let places = elements
            .map((el) => ({
                id: el.id,
                name: el.tags.name,
                lat: el.lat,
                lon: el.lon,
                place: el.tags.place,
            }));

        // Fallback to Nominatim bbox search if Overpass yields nothing (timeout/empty)
        if (!places || places.length === 0) {
            places = await nominatimFallback();
        }

        return places;
    } catch (error) {
        console.error("BBox city search error:", error);
        // Try Nominatim on errors too
        const places = await nominatimFallback();
        return places;
    }
};

/**
 * Fetch weather for a single city using OpenWeather's /weather endpoint with coordinates.
 * This is the fallback when /box/city is not available on the subscription tier.
 * 
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} cityName - City name (for logging/identification)
 * @returns {Promise<{ id: number|null, name: string, lat: number, lon: number, temp: number|null, conditionId: number|null, conditionMain: string, conditionDescription: string }|null>}
 */
const fetchSingleCityWeatherOpenWeather = async (lat, lon, cityName = "Unknown") => {
    try {
        const url = "https://api.openweathermap.org/data/2.5/weather";
        const params = {
            lat: lat,
            lon: lon,
            units: "metric",
            appid: OPENWEATHER_API_KEY,
        };

        const response = await axios.get(url, { params });

        return {
            id: response?.data?.id ?? null,
            name: response?.data?.name || cityName,
            lat: response?.data?.coord?.lat ?? lat,
            lon: response?.data?.coord?.lon ?? lon,
            temp: response?.data?.main?.temp ?? null,
            conditionId: response?.data?.weather?.[0]?.id ?? null,
            conditionMain: response?.data?.weather?.[0]?.main ?? "",
            conditionDescription: response?.data?.weather?.[0]?.description ?? "",
        };
    } catch (error) {
        // Don't throw - return null so we can continue with other cities
        console.warn(`⚠️ OpenWeather fetch failed for ${cityName} (${lat}, ${lon}):`, error.response?.status, error.response?.data?.message || error.message);
        return null;
    }
};

/**
 * Bulk weather fetch for multiple cities using OpenWeather's /weather endpoint.
 * Since /box/city requires a higher subscription tier, we use parallel calls to /weather with coordinates.
 * This batches requests to avoid rate limits.
 *
 * @param {Array<{ lat: number, lon: number, name?: string, id?: number|string }>} cities - Array of city objects with coordinates
 * @param {number} batchSize - Number of parallel requests per batch (default: 10)
 * @param {number} delayBetweenBatches - Delay in ms between batches (default: 200)
 * @returns {Promise<Array<{ id: number|null, name: string, lat: number, lon: number, temp: number|null, conditionId: number|null, conditionMain: string, conditionDescription: string }>>}
 */
export const fetchCitiesWeatherBatchOpenWeather = async (
    cities,
    batchSize = 10,
    delayBetweenBatches = 200
) => {
    if (!Array.isArray(cities) || cities.length === 0) {
        return [];
    }

    console.log(`🌤️ Fetching weather for ${cities.length} cities using OpenWeather /weather endpoint (batched)`);

    const results = [];

    // Process in batches to avoid rate limits
    for (let i = 0; i < cities.length; i += batchSize) {
        const batch = cities.slice(i, i + batchSize);
        const promises = batch.map(city =>
            fetchSingleCityWeatherOpenWeather(city.lat, city.lon, city.name || "Unknown")
        );

        const batchResults = await Promise.allSettled(promises);

        // Extract successful results
        batchResults.forEach((result, idx) => {
            if (result.status === 'fulfilled' && result.value !== null) {
                results.push(result.value);
            }
        });

        // Small delay between batches to respect rate limits
        if (i + batchSize < cities.length) {
            await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
    }

    console.log(`✅ OpenWeather batch fetch completed: ${results.length}/${cities.length} cities succeeded`);
    return results;
};

/**
 * Bulk weather fetch for all cities in a bounding box using OpenWeather.
 * First gets cities from Overpass/Nominatim, then fetches weather for each using /weather endpoint.
 *
 * @param {number} minLon
 * @param {number} minLat
 * @param {number} maxLon
 * @param {number} maxLat
 * @param {number} zoom - Current map zoom (used to limit number of cities)
 * @param {number} limit - Optional hard cap on returned cities (client-side)
 * @returns {Promise<Array<{ id: number|null, name: string, lat: number, lon: number, temp: number|null, conditionId: number|null, conditionMain: string, conditionDescription: string }>>}
 */
export const fetchCitiesWeatherInBBoxOpenWeather = async (
    minLon,
    minLat,
    maxLon,
    maxLat,
    zoom,
    limit = 60
) => {
    try {
        // First, get cities from Overpass/Nominatim (we already have this function)
        const cities = await searchCitiesInBBox(minLon, minLat, maxLon, maxLat, limit);

        if (!Array.isArray(cities) || cities.length === 0) {
            console.warn("⚠️ No cities found in bbox for OpenWeather fetch");
            return [];
        }

        // Now fetch weather for each city using parallel batches
        const weatherData = await fetchCitiesWeatherBatchOpenWeather(cities, 10, 200);

        return weatherData.slice(0, limit);
    } catch (error) {
        console.error("❌ OpenWeather bbox city weather error:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw error;
    }
};

export const getLocationSales = async (location) => {
    const config = {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${getUserToken()}`,
        },
    };
    const response = await axios.get(`${getLocationSalesApi}${location}`, config);
    return response.data;
};